import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Home as HomeIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import AlertModal from "../../components/AlertModal";
import { hasWritePermission } from '../../utils/menuFilter';
import ConfirmModal from "../../components/ConfirmModal";

const LandType = () => {
  const canWrite = hasWritePermission('master.general-master');
  const [landTypeData, setLandTypeData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [errors, setErrors] = useState({});

  // Modal alert/confirm states
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ title: '', message: '' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedItemForDelete, setSelectedItemForDelete] = useState(null);

  const [openModal, setOpenModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState({
    name: '',
    description: '',
    status: true,
  });

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // Fetch land types
  const fetchLandTypes = async (silent = false) => {
    if (!silent) setIsLoading(true);
    setApiError(null);
    try {
      const response = await fetch(
        `${API_BASE}/api/globalMasterDetails?type=landType`,
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const result = await response.json();
      const landTypes = Array.isArray(result.data)
        ? result.data.map((item) => ({
          id: item._id,
          landTypeId: item.details?.landTypeId || item._id.substring(0, 8).toUpperCase(),
          name: item.details?.name || 'Unknown',
          description: item.details?.description || '',
          status: item.status === true ? 'Active' : 'Inactive',
          rawStatus: item.status,
        }))
        : [];

      setLandTypeData(landTypes);
    } catch (err) {
      setApiError('Failed to load land types.');
      setLandTypeData([]);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLandTypes();
  }, []);

  // Filter logic
  const getFilteredLandTypes = () => {
    return landTypeData.filter((item) => {
      const matchesSearch =
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = !statusFilter || item.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  };

  const filteredLandTypes = getFilteredLandTypes();

  // Pagination
  const paginatedLandTypes = filteredLandTypes.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  // Validation
  const validateForm = () => {
    const err = {};
    if (!newItem.name.trim()) err.name = 'Land Type Name is required';
    return err;
  };

  // Duplicate check
  const isDuplicate = () => {
    return landTypeData.some(
      (item) =>
        item.name.toLowerCase() === newItem.name.trim().toLowerCase() &&
        item.id !== editingItem?.id,
    );
  };

  // Handlers
  const handleOpenModal = () => {
    setEditingItem(null);
    setNewItem({
      name: '',
      description: '',
      status: true,
    });
    setErrors({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingItem(null);
    setNewItem({
      name: '',
      description: '',
      status: true,
    });
    setErrors({});
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setNewItem({
      name: item.name,
      description: item.description,
      status: item.rawStatus,
    });
    setErrors({});
    setOpenModal(true);
  };

  const handleDeleteClick = (item) => {
    setSelectedItemForDelete(item);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedItemForDelete) return;

    setIsLoading(true);
    setConfirmOpen(false);
    try {
      const response = await fetch(`${API_BASE}/api/globalMasterDetails/${selectedItemForDelete.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isDeleted: true }),
      });

      if (!response.ok) throw new Error('Delete failed');

      setAlertConfig({ title: 'Success', message: 'Land Type deleted successfully!' });
      setAlertOpen(true);
      fetchLandTypes(true);
    } catch (err) {
      setAlertConfig({ title: 'Error', message: 'Failed to delete land type.' });
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
      setSelectedItemForDelete(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (isDuplicate()) {
      setErrors({ name: 'Land Type name already exists' });
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        type: 'landType',
        details: {
          name: newItem.name.trim(),
          description: newItem.description.trim(),
        },
        status: newItem.status,
      };

      let response;
      if (editingItem) {
        response = await fetch(`${API_BASE}/api/globalMasterDetails/${editingItem.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        response = await fetch(`${API_BASE}/api/globalMasterDetails`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      if (!response.ok) throw new Error('Save failed');

      setAlertConfig({
        title: 'Success',
        message: editingItem ? 'Land Type updated successfully!' : 'Land Type added successfully!'
      });
      setAlertOpen(true);
      handleCloseModal();
      fetchLandTypes(true);
    } catch (err) {
      setApiError('Failed to save land type.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSearchQuery('');
    setStatusFilter('');
    setPage(0);
    fetchLandTypes(true);
  };

  const handleChange = (field, value) => {
    setNewItem((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HomeIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
            Land Type Master
          </Typography>
        </Box>
         <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchLandTypes()}
            disabled={isLoading}
            sx={{ flex: { xs: 1, sm: 'none' }, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Refresh
          </Button>
          {canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenModal}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, flex: { xs: 1, sm: 'none' } }}
            >
              Add New Land Type
            </Button>
          )}
        </Box>
      </Box>

      {/* Modal alert/confirm states */}
      <AlertModal
        open={alertOpen}
        title={alertConfig.title}
        message={alertConfig.message}
        onClose={() => setAlertOpen(false)}
      />

      <ConfirmModal
        open={confirmOpen}
        title="Confirm Delete"
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        confirmText="Delete"
        cancelText="Cancel"
        type="error"
      >
        Are you sure you want to delete the land type <strong>{selectedItemForDelete?.name}</strong>? This action cannot be undone.
      </ConfirmModal>

       {apiError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {apiError}
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => fetchLandTypes()}
            sx={{ ml: 2 }}
            size="small"
            variant="outlined"
          >
            Retry
          </Button>
        </Alert>
      )}

      {/* Search & Filter Section */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search Land Types..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                  label="Status"
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
             <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    const count = filteredLandTypes.length;
                    setAlertConfig({
                      title: 'Search Results',
                      message: count > 0 ? `Found ${count} records matching your criteria.` : 'No records found matching your criteria.'
                    });
                    setAlertOpen(true);
                  }}
                  startIcon={<SearchIcon />}
                  sx={{ textTransform: 'none', height: '56px', borderRadius: 2, fontWeight: 600 }}
                >
                  Search
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleReset}
                  sx={{ textTransform: 'none', height: '56px', borderRadius: 2, fontWeight: 600 }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading && !landTypeData.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedLandTypes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {isLoading ? 'Loading...' : 'No land types found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedLandTypes.map((item, index) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>
                        <Tooltip title={item.description}>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {item.description || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.status}
                          color={item.status === 'Active' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {canWrite && (
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEdit(item)}
                              disabled={isLoading}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canWrite && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(item)}
                              disabled={isLoading}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredLandTypes.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Paper>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit Land Type' : 'Add New Land Type'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Land Type Name"
                  value={newItem.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  error={!!errors.name}
                  helperText={errors.name}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={newItem.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={newItem.status}
                    onChange={(e) => handleChange('status', e.target.value)}
                    label="Status"
                  >
                    <MenuItem value={true}>Active</MenuItem>
                    <MenuItem value={false}>Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
           <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseModal} disabled={isLoading} sx={{ textTransform: 'none', fontWeight: 600 }}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={isLoading} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>
              {editingItem ? 'Update' : 'Add'} Land Type
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default LandType;
